/**
 * Phrase preview — a slide-in panel showing everything the app has cached about
 * one phrase, opened by id from anywhere in the app:
 *
 *   import { openPhrasePreview } from '../components/phrasePreview.js';
 *   openPhrasePreview(phraseId);
 *
 * Deliberately **independent** of any screen: it appends its own overlay to
 * `<body>` (like `confirmDialog`) instead of living inside a view's mount, owns
 * its own teardown, and needs nothing from its caller but the id. It slides in
 * from the **left** — the mirror image of the shell's right-hand menu drawer, so
 * the direction alone says which panel opened.
 *
 * It never touches the network: the phrase is read from the `phrases` store, and
 * a phrase that isn't cached simply shows an empty state. Audio comes from the
 * row's `audio_blob` and goes through the app-wide Player, so opening this panel
 * and pressing play ends whatever was playing before (the Player has one active
 * source); closing the panel stops the clip if it was still ours.
 */

import { getPhrase } from '../study/content.js';
import { isInDictionary, toggleDictionary } from '../study/dictionary.js';
import { escapeHtml } from '../utils/html.js';
import { ICONS } from './icons.js';
import { emptyState, loaderView } from './feedback.js';
import { playButtonHtml } from './playButton.js';
import { togglePillHtml, wireTogglePill } from './togglePill.js';
import { pause, play, resume, stop, subscribe } from './player.js';

const PANEL_ID = 'phrase-preview';

const DICTIONARY_LABELS = ['Dodaj do słownika', 'Usuń ze słownika'];
const DICTIONARY_ICONS = [ICONS.star('h-5 w-5'), ICONS.starFilled('h-5 w-5')];

/** Teardown of the panel currently on screen, if any — one at a time. */
let activeClose = null;

/* -------------------------------------------------------------------------- */
/* Markup                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The panel chrome: backdrop + the left-hand sheet, with an empty body the
 * IndexedDB read fills in. Mirrors the shell drawer's `data-state` +
 * `group-data-[state=open]` transition, flipped to slide from the left.
 */
function panelHtml() {
  return `
    <div data-backdrop
      class="absolute inset-0 bg-neutral-900/40 opacity-0 backdrop-blur-sm transition-opacity duration-300 group-data-[state=open]:opacity-100"></div>

    <aside role="dialog" aria-modal="true" aria-label="Podgląd zwrotu"
      class="absolute inset-y-0 left-0 flex w-80 max-w-[85%] -translate-x-full flex-col bg-white shadow-soft-lg transition-transform duration-300 ease-out group-data-[state=open]:translate-x-0"
      style="padding-top: env(safe-area-inset-top); padding-bottom: env(safe-area-inset-bottom);">
      <div class="flex items-center justify-end px-5 pb-1 pt-4">
        <button data-close type="button" aria-label="Zamknij podgląd"
          class="grid h-9 w-9 place-items-center rounded-xl text-neutral-500 transition active:scale-95 active:bg-neutral-100">
          ${ICONS.close()}
        </button>
      </div>

      <div data-body class="flex-1 overflow-y-auto px-5 pb-5"></div>
    </aside>`;
}

/**
 * Dictionary toggle, beneath the phrase — the shared labelled pill the topic
 * header uses for favorites, but starred rather than hearted: the heart stays
 * with favorite *topics*, the star marks a saved *phrase*.
 */
function dictionaryButtonHtml() {
  return `<div class="mt-4">
    ${togglePillHtml({ offLabel: DICTIONARY_LABELS[0], offIcon: DICTIONARY_ICONS[0] })}
  </div>`;
}

/**
 * Turn each quoted fragment of a `description` into bold text: the quotes there
 * always wrap the forms the explanation contrasts ("one day early" vs. "one day
 * earlier"), which read better emphasised than punctuated.
 *
 * Takes the **already escaped** string and splits on the entity, so the only
 * markup that can reach the DOM is the `<strong>` added here — escaping stays the
 * single XSS defense. Quotes are only consumed in pairs; an unpaired one is left
 * alone as a quote.
 */
function boldQuoted(escaped) {
  const parts = escaped.split('&quot;');
  if (parts.length < 3) return escaped; // fewer than two quotes → nothing to pair
  return parts
    .map((part, index) => {
      if (index % 2 === 0) return part; // outside the quotes
      return index + 1 < parts.length ? `<strong>${part}</strong>` : `&quot;${part}`;
    })
    .join('');
}

/** A titled block, used for the description and the examples. */
function sectionHtml(title, inner) {
  return `
    <div class="mt-6">
      <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">${title}</h3>
      ${inner}
    </div>`;
}

/** `examples[]` — each an English sentence with its Polish translation. */
function examplesHtml(examples) {
  const list = (Array.isArray(examples) ? examples : []).filter(Boolean);
  if (!list.length) return '';
  return sectionHtml(
    'Przykłady',
    `<ul class="flex flex-col gap-2">
       ${list
         .map(
           (example) => `
        <li class="rounded-xl bg-cream px-3 py-2.5">
          <p class="text-sm font-medium leading-snug text-neutral-800">${escapeHtml(example.sentence ?? '')}</p>
          ${
            example.translation
              ? `<p class="mt-1 text-sm leading-snug text-neutral-600">${escapeHtml(example.translation)}</p>`
              : ''
          }
        </li>`
         )
         .join('')}
     </ul>`
  );
}

/**
 * The phrase and its translation, the play control, then the explanation and the
 * examples. What the row also stores but this doesn't show: its level, and the
 * plumbing (`topicId` / `levelId` / positions / the audio path).
 */
function phraseBodyHtml(phrase) {
  const translation = phrase.translation
    ? `<p class="mt-1.5 text-base leading-snug text-neutral-600">${escapeHtml(phrase.translation)}</p>`
    : '';
  const description = phrase.description
    ? sectionHtml(
        'Wyjaśnienie',
        `<p class="text-sm leading-relaxed text-neutral-700 [&_strong]:font-semibold [&_strong]:text-neutral-900">
           ${boldQuoted(escapeHtml(phrase.description))}
         </p>`
      )
    : '';

  return `
    <div class="flex items-start gap-2">
      ${playButtonHtml({
        hook: 'data-play',
        label: 'Odtwórz zwrot',
        hasAudio: phrase.audio_blob instanceof Blob,
        className: 'mt-1',
      })}
      <div class="min-w-0 flex-1">
        <h2 class="text-xl font-semibold leading-snug tracking-tight text-neutral-900">
          ${escapeHtml(phrase.content ?? '')}
        </h2>
        ${translation}
      </div>
    </div>
    ${dictionaryButtonHtml()}
    ${description}
    ${examplesHtml(phrase.examples)}`;
}

/** Shown when the id has no row in the `phrases` store (nothing was cached). */
function missingBodyHtml() {
  return emptyState(
    'Nie znaleziono zwrotu',
    'Ten zwrot nie jest zapisany w pamięci urządzenia. Otwórz jego temat, aby go pobrać.'
  );
}

/* -------------------------------------------------------------------------- */
/* Panel                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Bind the body's play control to the shared Player.
 *
 * The button reflects *our* clip only: `currentId` tells us whether what the
 * player is doing is this phrase, so a clip started elsewhere never relabels this
 * button. Returns a teardown that detaches and stops our own playback.
 */
function wirePlay(body, phrase) {
  const btn = body.querySelector('[data-play]');
  if (!btn || !(phrase.audio_blob instanceof Blob)) return () => {};

  let state = { status: 'idle', currentId: null };
  const isOurs = () => state.currentId === phrase.id;

  const unsubscribe = subscribe((next) => {
    state = next;
    const playing = isOurs() && next.status === 'playing';
    btn.innerHTML = playing ? ICONS.pause('h-4 w-4') : ICONS.play('h-4 w-4');
    btn.setAttribute('aria-label', playing ? 'Wstrzymaj' : 'Odtwórz zwrot');
  });

  btn.addEventListener('click', () => {
    if (isOurs() && state.status === 'playing') return pause();
    if (isOurs() && state.status === 'paused') return resume();
    play([{ id: phrase.id, blob: phrase.audio_blob }]);
  });

  return () => {
    // Leave anything that isn't ours alone — the panel only owns its own clip.
    if (isOurs()) stop();
    unsubscribe();
  };
}

/**
 * Bind the dictionary toggle to the `dictionary` store — the shared pill, so it
 * behaves exactly like the topic header's favorite toggle.
 */
function wireDictionary(body, phrase) {
  return wireTogglePill(body, {
    read: () => isInDictionary(phrase.id),
    write: () => toggleDictionary(phrase.id),
    labels: DICTIONARY_LABELS,
    icons: DICTIONARY_ICONS,
    onClass: 'text-amber-500',
  });
}

/**
 * Open the preview for one phrase. Safe to call at any time and from any screen;
 * a second call replaces whatever panel is open.
 *
 * @param {string} phraseId Phrase id, as stored in the `phrases` store.
 * @param {{ onClose?: () => void }} [options]
 *   - onClose: called once the panel is gone, however it went (✕, backdrop,
 *     Escape, navigation, or being replaced). The panel can change the phrase's
 *     dictionary state, so a screen listing the dictionary uses this to bring its
 *     rows back in step.
 */
export function openPhrasePreview(phraseId, { onClose } = {}) {
  if (!phraseId) return;
  activeClose?.(false); // replacing an open panel — no exit animation

  const overlay = document.createElement('div');
  overlay.id = PANEL_ID;
  overlay.dataset.state = 'closed';
  overlay.className =
    'group fixed inset-0 z-[60] pointer-events-none data-[state=open]:pointer-events-auto';
  overlay.innerHTML = panelHtml();

  const body = overlay.querySelector('[data-body]');
  body.innerHTML = loaderView('Wczytywanie…', { fullscreen: false });

  const root = document.documentElement;
  const wasLocked = root.classList.contains('overflow-hidden');
  const app = document.getElementById('app');
  const previouslyFocused = document.activeElement;
  let teardownPlay = () => {};

  const close = (animate = true) => {
    if (activeClose !== close) return; // already closed / replaced
    activeClose = null;
    teardownPlay();
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('hashchange', onNavigate);
    app?.removeAttribute('inert');
    if (!wasLocked) root.classList.remove('overflow-hidden');
    if (animate) {
      overlay.dataset.state = 'closed';
      setTimeout(() => overlay.remove(), 300); // matches the slide-out duration
      previouslyFocused?.focus?.();
    } else {
      overlay.remove();
    }
    onClose?.();
  };

  const onKey = (event) => {
    if (event.key === 'Escape') close();
  };
  // The panel belongs to no route, so a navigation underneath it dismisses it.
  const onNavigate = () => close(false);

  overlay.addEventListener('click', (event) => {
    if (event.target.closest('[data-close]') || event.target.closest('[data-backdrop]')) close();
  });
  document.addEventListener('keydown', onKey);
  window.addEventListener('hashchange', onNavigate);

  root.classList.add('overflow-hidden');
  app?.setAttribute('inert', ''); // focus trap: the screen behind is unreachable
  document.body.appendChild(overlay);
  activeClose = close;

  // Next frame → run the enter transition and move focus into the panel.
  requestAnimationFrame(() => {
    overlay.dataset.state = 'open';
    overlay.querySelector('[data-close]')?.focus();
  });

  // Fill the body from IndexedDB. Awaited after the panel is already sliding in,
  // so the read never delays the animation.
  (async () => {
    let phrase;
    try {
      phrase = await getPhrase(phraseId);
    } catch {
      /* store unavailable — treat as "nothing cached" */
    }
    if (activeClose !== close) return; // closed / replaced while reading
    if (!phrase) {
      body.innerHTML = missingBodyHtml();
      return;
    }
    body.innerHTML = phraseBodyHtml(phrase);
    teardownPlay = wirePlay(body, phrase);
    wireDictionary(body, phrase);
  })();
}
